import { JSONPath } from "jsonpath-plus";
import {
  createSessionTable,
  Pointer,
  PointerTable,
  PointerTableEntry,
  SchemaPointerTable,
} from "./pointerTables";
import {
  IS_JSONPATH_FILTER_REGEX,
  TEMPLATE_VAR_REGEX,
  TEMPLATE_VAR_REGEX_NON,
  TEMPLATE_VAR_SQR,
} from "./const";
import type {
  DefaultSchema,
  ExpandedVariablesTuple,
  ExtractEntryValuesTuple,
  Json,
  PathMutatorConfigValues,
  TransformationConfig,
  TransformationConfigObject,
  TransformationEntry,
  TransformationStackEntry,
  ValidPathType,
} from "./types";
import "array-flat-polyfill";

/**
 *
 * @param {ValidPathType} pathType Acts as a director for which table to query
 */
function validateType(pathType: ValidPathType) {
  if (!pathType.match(/(in|out)putPath|schema|path/)) {
    throw new Error(
      `type recieved unexpected value [${pathType}], expected inputPath, outputPath, or schema`
    );
  }
}

/**
 * This functions by building a table of live pointers via memoization. JSONPath callback
 * returns the parent and parentProperty we use these along with path and store them in a
 * table with the key of the path. The invariant is that the output shape remains constant.
 *
 * Every execution the inputPath table is reset since each input could be unique.
 * Schema table is built as requests are made
 * Paths table is for collections, it memoizes based on templating via #![var]!#
 *   the code will expand path requests to those template identifiers then memoize them
 *    in the paths table. The lifetime is as long as the pathMutator object
 *
 * All these pointer tables are contaiend within sessionTable object
 *
 * Due to the nature of pointers this cannot be serialized without an init cost. More efficient
 * to be as required.
 *
 * For each type:
 *  - outputPathTable: Pointers to places within the output object (jsonSrc) with the path used as the key
 *  - inputPathTable:  Pointers to places within the input object (passed every call) with the path used as the key and reset every call
 *  - pathTable:       Pointers to resolved paths within the output object, created specifically for collections
 *  - schemaTable:     Pointers to places in schema where outputPath target
 *
 * A note on collections: They are the real source of complexity since they can move around and make our state become stale, a pro
 * of utilizing pointers is that the index does not matter. Instead the pointer will give the location of the data regardless. So
 * while the path might say one thing the pointer could point somewhere else. We anticipate this should be an issue as when collections
 * are cleared so is are the entries.
 *
 * @param jsonSrc Source JSON where pointers reference
 * @param schemaObj Schema to reference when building entries
 * @returns {ReturnType<typeof pathMutator>}
 */
export function pathMutator<T extends Json, S extends DefaultSchema>(
  jsonSrc: T,
  schemaObj = {} as S
) {
  return ((json: T, schema: S) => {
    let sessionTable = createSessionTable();
    let configValues: PathMutatorConfigValues<T, S> = {
      schema,
      json,
      inputs: {},
      debug: false,
    };

    function getValueAtPath(path: string, pathType: ValidPathType) {
      validateType(pathType);

      if (pathType === "inputPath") {
        return sessionTable.getInputPath(path);
      } else {
        return sessionTable.getOutputPath(path);
      }
    }

    function setValueAtPath(
      path: string,
      value: PointerTable[string],
      pathType: ValidPathType
    ) {
      validateType(pathType);

      if (pathType === "inputPath") {
        sessionTable.setInputPath(path, value);
      } else {
        sessionTable.setOutputPath(path, value);
      }
    }

    function extractEntryValues(
      value: TransformationEntry,
      path: string
    ): ExtractEntryValuesTuple {
      const {
        inputPath,
        outputPath,
        default: defaultValue,
        ...otherProps
      } = value;
      const isEntry =
        inputPath !== undefined ||
        outputPath !== undefined ||
        defaultValue !== undefined;

      return [
        {
          inputPath,
          outputPath: outputPath || path,
          default: defaultValue,
        },
        isEntry,
        otherProps as { [prop: string]: Json },
        path,
      ];
    }

    /**
     * This will attempt to crawl the schema and create what is necessary at the output point. Essentially allowing the consumer
     * to create new entries on request. While there might be some extra padding it will be small enough it is neglible. If there
     * is an occasional empty object we can deal with that later.
     *
     * If the schema path doesn't exist an error will be thrown
     * containing could not create. If a filterPath or currentPath pointer is not found another error will be thrown expecting one
     * of those exists.
     *
     * @param outputPath Place in shape to place data
     * @param schemaPath Place in schema to search for data to place in output
     * @returns {PointerTableEntry}
     */
    async function tryToCreate(
      outputPath: string,
      schemaPath?: string
    ): Promise<PointerTable[string] | Pointer> {
      const stack = JSONPath.toPathArray(outputPath);
      stack.pop();
      while (stack.length > 0) {
        try {
          const currentPath = stack.reduce((pathStr, currentNode) => {
            return isFilter(currentNode) || isTemplate(currentNode)
              ? `${pathStr}[${currentNode}]`
              : `${pathStr}.${currentNode}`;
          });
          const filterPointer = isFilter(currentPath)
            ? sessionTable.getPathValue(currentPath)
            : undefined;
          const schemaShape = await fetchValueFromSchema(
            schemaPath ?? currentPath
          );

          const pathPointer = await fetchDataAtPath(currentPath, "outputPath");
          if (
            (pathPointer !== undefined || filterPointer !== undefined) &&
            schemaShape
          ) {
            const pointerToUse = filterPointer ?? pathPointer;
            if (pointerToUse === undefined) {
              throw new Error(`Expected pointerTouse to be defined`);
            }

            if (Array.isArray(pointerToUse.data)) {
              const newObj = JSON.parse(JSON.stringify(schemaShape.data));

              const position = pointerToUse?.data.push(newObj as Pointer);
              const arrPointer = {
                parent: pointerToUse?.data,
                parentProperty: position - 1,
                data: newObj,
              };
              sessionTable.setOutputPath(
                outputPath,
                arrPointer as unknown as PointerTable[string]
              );
              return arrPointer;
            } else {
              const target =
                pointerToUse.parentProperty === null
                  ? pointerToUse.data
                  : pointerToUse.parent[pointerToUse.parentProperty];
              if (schemaShape.arrays && schemaShape.arrays.length === 1) {
                Object.assign(target, {
                  [schemaShape.arrays[0]]: schemaShape.data,
                });
              } else {
                Object.assign(target, {
                  ...schemaShape.data,
                  ...pointerToUse.data,
                });
              }
              return pointerToUse;
            }
          }
        } catch (e) {
          if (configValues.debug) {
            console.error(e);
          }
        } finally {
          stack.pop();
        }
      }

      throw new Error("Could not create!");
    }

    /**
     * @typedef {Object} ObjectPointer
     * @property {any} data
     * @property {String} parentProperty
     * @property {Object} parent
     */
    function fetchDataAtPath(
      path: string,
      pathType: ValidPathType
    ): Promise<PointerTable[string]> {
      return new Promise((res, rej) => {
        validateType(pathType);

        const memoizedValue =
          path !== "" ? getValueAtPath(path, pathType) : undefined;

        if (memoizedValue) {
          return res(memoizedValue);
        }

        if (path === undefined) {
          return rej(Error(`Path required for type [${pathType}]`));
        }

        if (TEMPLATE_VAR_REGEX.test(path)) {
          return rej(Error(`featchDataAtPath does not support filter`));
        }

        let targetJson =
          pathType === "inputPath"
            ? // This fallback is incase we lneed to pull from an obj
              (configValues.inputs ?? configValues.json)
            : configValues.json;
        let pathResult = JSONPath({
          path,
          json: targetJson,
          callback: (payload, _, fullPayload) => {
            const { parent, parentProperty } = fullPayload;
            const objectPointer = {
              data: payload,
              parentProperty,
              parent,
            } as PointerTable[string];
            setValueAtPath(path, objectPointer, pathType);
            res(objectPointer);
          },
        });

        if (pathResult.length === 0) {
          return rej(Error(`Nothing found at ${pathType} [${path}]`));
        }
      });
    }

    // const test = /#!(.+)!#/gi;
    function isTemplate(path?: string) {
      TEMPLATE_VAR_REGEX_NON.lastIndex = 0;

      return TEMPLATE_VAR_REGEX_NON.test(path);
    }

    function isFilter(path?: string) {
      IS_JSONPATH_FILTER_REGEX.lastIndex = 0;
      // This can accept undefined
      return IS_JSONPATH_FILTER_REGEX.test(path);
    }

    function getSchemaPath(path: string) {
      if (isFilter(path)) {
        return path.replace(IS_JSONPATH_FILTER_REGEX, "[0]");
      } else {
        TEMPLATE_VAR_REGEX_NON.lastIndex = 0;
        const pathResult = TEMPLATE_VAR_REGEX_NON.test(path);
        if (pathResult) {
          TEMPLATE_VAR_SQR.lastIndex = 0;
          return path.replace(
            TEMPLATE_VAR_SQR,
            (match, ...cgs) =>
              `.${cgs[0]}${
                cgs[cgs.length - 2] + match.length < path.length ? "." : ""
              }`
          );
        }
        return path;
      }
    }

    /**
     * @typedef {Object} Entry
     * @property {String} inputPath
     * @property {String} outputPath
     * @property {String | Number | Boolean} default
     * @property {String} mutate
     */

    function fetchValueFromSchema(
      path: string
    ): Promise<SchemaPointerTable[string]> {
      return new Promise((res, rej) => {
        if (path === undefined) {
          return rej(Error(`Nothing found at path [${path}] in schema`));
        }

        const pathToUse = getSchemaPath(path);
        const memoizedSchemaValue = sessionTable.getSchemaValue(pathToUse);

        if (memoizedSchemaValue && memoizedSchemaValue.data) {
          // Have to make new instances of arrays for this to work as expected
          const newSchema = memoizedSchemaValue.arrays.reduce(
            (final, key) => {
              final[key] = [];
              return final;
            },
            {} as { [prop: string]: [] }
          );
          return res({
            ...memoizedSchemaValue,
            data: { ...memoizedSchemaValue.data, ...newSchema },
          });
        }

        const data = JSONPath({
          path: pathToUse,
          json: configValues.schema,
          // Since this relies on there only being one match ever this implementation can be ported
          // to any JSONPath implementation
          callback: (payload: Pointer, _, full) => {
            const { parentProperty } = full;
            const arrays: string[] = [];
            if (typeof payload === "object") {
              const valueToUse = Array.isArray(payload) ? payload[0] : payload;
              if (Array.isArray(payload)) {
                const arrPointer = {
                  data: payload as Pointer,
                  arrays: [parentProperty],
                };
                sessionTable.setSchemaValue(pathToUse, arrPointer);
                res(arrPointer);
                return;
              }

              const transformedSchema = Object.keys(valueToUse).reduce(
                (schema, schemaKey) => {
                  if (Array.isArray(valueToUse[schemaKey])) {
                    // schema[schemaKey] = [];
                    arrays.push(schemaKey);
                  }
                  schema[schemaKey] = valueToUse[schemaKey];

                  return schema;
                },
                {} as {
                  // prettier-ignore
                  [K in keyof typeof payload]: typeof payload[K] extends Array<any>
                    ? []
                    : typeof payload[K];
                }
              );
              const schemaValue = {
                data: transformedSchema,
                arrays,
              };
              sessionTable.setSchemaValue(pathToUse, schemaValue);
              res(schemaValue);
            } else {
              res({ data: { [parentProperty]: payload }, arrays: [] });
            }
          },
        });

        if (data.length === 0) {
          rej(Error(`Path [${pathToUse}] not in schema`));
        }
      });
    }

    function expandFilterVariables(
      inputNode: Pointer[],
      regex: RegExp,
      filterString: string,
      captureGroup = 1
    ) {
      const inputArray: Pointer[] = Array.isArray(inputNode)
        ? inputNode
        : Object.values(inputNode);
      return inputArray.map<ExpandedVariablesTuple>((currentInputNode) => {
        TEMPLATE_VAR_SQR.lastIndex = 0;
        const schemaPathToUse = TEMPLATE_VAR_SQR.test(filterString)
          ? filterString.replace(TEMPLATE_VAR_SQR, (_, ...captureGroups) => {
              return `[${captureGroups[0]}]`;
            })
          : undefined;
        regex.lastIndex = 0;
        const transformedString = filterString.replace(
          regex,
          (_, ...captureGroups) => {
            const varToFill =
              currentInputNode[
                captureGroups[captureGroup - 1]
                // This pulls a value out of pointer
              ];

            return typeof varToFill === "string"
              ? `"${varToFill}"`
              : (varToFill as unknown as string);
          }
        );

        return [transformedString, currentInputNode, schemaPathToUse];
      });
    }

    async function fetchMemoizedPath(
      path: string,
      collectionKey?: string,
      schemaPath?: string
    ) {
      if (path == undefined) {
        throw new Error(`Path required to query Paths objects!`);
      }

      const pathToQuery =
        collectionKey === undefined ? path : `${path}.${collectionKey}`;
      const memoizedPathValue = sessionTable.getPathValue(pathToQuery);
      if (memoizedPathValue && memoizedPathValue.data) {
        return memoizedPathValue;
      }

      // Get from schema
      try {
        const pointer = await fetchDataAtPath(pathToQuery, "outputPath");
        sessionTable.setPathValue(pathToQuery, pointer);
        return pointer;
      } catch (_e) {
        const createdPointer = await tryToCreate(
          pathToQuery,
          schemaPath ?? getSchemaPath(pathToQuery)
        );
        if (collectionKey) {
          const collectionPointer: PointerTableEntry = {
            data: createdPointer.data[collectionKey],
            arrays: [collectionKey],
            parent: createdPointer.data as Pointer,
            parentProperty: collectionKey,
          };
          sessionTable.setPathValue(pathToQuery, collectionPointer);
          return collectionPointer;
        } else {
          sessionTable.setPathValue(
            pathToQuery,
            createdPointer?.data
              ? (createdPointer as PointerTableEntry)
              : ({ data: createdPointer } as PointerTableEntry)
          );
          return createdPointer;
        }
      }
    }

    function processEntry(entryValues: TransformationEntry, path: string) {
      if (configValues.debug) {
        console.log("With Entry");
      }

      return Promise.all([
        fetchDataAtPath(entryValues.inputPath ?? path, "inputPath").catch(
          (e) => {
            if (configValues.debug) {
              console.error(e);
            }
            return entryValues.default
              ? {
                  data: entryValues.default,
                }
              : undefined;
          }
        ),
        fetchDataAtPath(entryValues.outputPath, "outputPath")
          .catch(() => {
            const isFilterRes = isFilter(entryValues.outputPath);
            const pathToUse = isFilterRes
              ? getSchemaPath(entryValues.outputPath)
              : undefined;

            return fetchValueFromSchema(pathToUse ?? entryValues.outputPath)
              .then(() => tryToCreate(entryValues.outputPath, pathToUse))
              .then((res) => {
                return fetchDataAtPath(entryValues.outputPath, "outputPath");
              })
              .catch((e) => {
                if (configValues.debug) {
                  console.error(e);
                }
              });
          })
          .catch((e) => {
            if (configValues.debug) {
              console.error(e);
            }
            return Promise.reject(
              Error(`Path [${entryValues.outputPath}] does not exist in schema`)
            );
          }),
      ]).then(([inputPath, outputPath]) => {
        if (configValues.debug) {
          console.log({
            inputPath,
            outputPath,
            entryValues,
          });
        }

        if (
          inputPath?.data !== undefined &&
          outputPath &&
          outputPath?.parent &&
          outputPath?.parentProperty
        ) {
          outputPath.parent[outputPath.parentProperty] = inputPath.data;
        }
      });
    }
    function setWithoutEntry(
      dataPath: string,
      outputPath: string,
      schemaPath?: string
    ) {
      if (configValues.debug) {
        console.log("Without Entry");
      }
      return fetchDataAtPath(dataPath, "inputPath")
        .then((inputData) => {
          return Promise.all([
            inputData,
            fetchDataAtPath(outputPath, "outputPath")
              .catch(() => {
                const pathToUse = schemaPath ?? outputPath;
                return fetchValueFromSchema(pathToUse)
                  .then(() => tryToCreate(outputPath, schemaPath))
                  .then(() => {
                    return fetchDataAtPath(outputPath, "outputPath");
                  });
              })
              .catch(() =>
                Error(`Path [${outputPath}] does not exist in schema`)
              ),
          ]);
        })
        .then(([valueToUse, pathPointer]) => {
          if (!pathPointer || !valueToUse || pathPointer instanceof Error) {
            if (pathPointer instanceof Error) {
              console.error(pathPointer);
            }
            throw new Error(
              `Expected valueToUse and pathPointer to be defined`
            );
          }
          // @ts-ignore
          (pathPointer.parent as Pointer)[pathPointer.parentProperty] =
            valueToUse?.data;
        })
        .catch((e) => {
          if (configValues.debug) {
            console.error(e);
          }
        });
    }

    async function processObjectNode(
      value: TransformationConfig,
      path: string
    ) {
      const [entryValues, isEntry, otherProps] = extractEntryValues(
        value as TransformationEntry,
        path
      );
      const processPromise = isEntry
        ? processEntry(entryValues, path)
        : Promise.resolve();

      return processPromise.then(() =>
        // Might change this to only append if it is a sub object to limit repition
        Object.keys(otherProps).map((otherProp) => [
          value,
          otherProp,
          otherProps[otherProp],
          `${path}.${otherProp}`,
        ])
      );
    }

    // TOOD: Update name
    async function getFilterInputNode(
      path: string,
      key: string,
      entry: TransformationEntry,
      filterParent?: SchemaPointerTable[string]
    ) {
      // Here if no parent exists then we create it!
      if (entry.inputPath === undefined && filterParent === undefined) {
        IS_JSONPATH_FILTER_REGEX.lastIndex = 0;
        const newCollectionKey = key.split(IS_JSONPATH_FILTER_REGEX)[0];
        let newCollection = path.slice(0, -key.length);
        if (newCollection.endsWith(".")) {
          newCollection = newCollection.slice(0, -1);
        }

        // This verifies the collection's parent is there and fetch so this will trim down to the parent of the key with the collection!
        // Ex: $.some.thing[?(@ && @.id == #!id!#)] -> $.some
        // Then query for that result to ensure it exists!
        return (
          fetchMemoizedPath(newCollection, newCollectionKey)
            .then((pointer) => {
              // Checking if parent node has existing collection, if not initialize empty collection
              if (Array.isArray(pointer.parent)) {
                pointer.parent
                  // @ts-ignore
                  .filter((entry) => entry[newCollectionKey] === undefined)
                  // @ts-ignore
                  .forEach((entry) => (entry[newCollectionKey] = []));
              }

              return pointer;
            })
            // This actually gets the expected input
            .then(() =>
              fetchDataAtPath(
                `${newCollection}.${newCollectionKey}`,
                "inputPath"
              )
            )
        );
      }

      // Assumes the parent exists somewhere 😉
      return filterParent ?? fetchDataAtPath(entry.inputPath, "inputPath");
    }

    /**
     * Will take the variable #![var]!# and expand the parent to contain
     * an entry with the key of var. Then the schema is used to fill in
     * the subobject. Below is an example of path containing
     * ...some[#!var#!]...
     *
     * @param path
     * @param key
     * @param entry
     * @param regex
     * @param templateParent
     */
    async function getTemplateInputNode(
      path: string,
      key: string,
      entry: TransformationEntry,
      templateParent?: SchemaPointerTable[string]
    ): Promise<
      [
        Promise<PointerTableEntry>,
        Promise<PointerTableEntry>,
        string | undefined,
      ]
    > {
      if (entry.inputPath === undefined && templateParent === undefined) {
        TEMPLATE_VAR_SQR.lastIndex = 0;
        // This piece needs to change too!
        const newParentEntryKey = key.split(TEMPLATE_VAR_SQR)[0];
        let newEntryGrandParentPath = path.slice(0, -key.length);
        if (newEntryGrandParentPath.endsWith(".")) {
          newEntryGrandParentPath = newEntryGrandParentPath.slice(0, -1);
        }
        // This verifies the parent entry is there and will fetch it.
        // First the path is trimed down to the parent (see below)
        // Ex: $.some.thing[?(@ && @.id == #!id!#)] -> $.some
        // Then query for that result to ensure it exists!
        return fetchMemoizedPath(newEntryGrandParentPath, newParentEntryKey)
          .then((pointer) => {
            if (Array.isArray(pointer.parent)) {
              // If the Parent Entry aka InputNode doesn't exist make it!
              pointer.parent
                .filter((entry) => entry[newParentEntryKey] === undefined)
                .forEach((entry) => (entry[newParentEntryKey] = {}));
            }

            // Return for use later maybe?
            return pointer;
          })
          .then(() => {
            const pathForPointer = `${newEntryGrandParentPath}.${newParentEntryKey}`;
            return [
              fetchDataAtPath(
                // This might need some adjustments
                pathForPointer,
                "inputPath"
              ),
              fetchDataAtPath(pathForPointer, "outputPath"),
              undefined,
            ];
          });
      }

      return [
        Promise.resolve(templateParent as PointerTableEntry) ??
          fetchDataAtPath(entry.inputPath, "inputPath"),
        fetchDataAtPath(path, "outputPath"),
        entry.inputPath,
      ];
    }

    function expandTemplateVariables(
      templateParentPointer: Pointer,
      templatePath: string,
      inputPath?: string
    ): Array<
      [string, string, Pointer["data"], string | undefined, string | undefined]
    > {
      if (typeof templateParentPointer !== "object") {
        throw new TypeError(
          "Expand Template Variables expects an object, recieved " +
            typeof templateParentPointer
        );
      }

      TEMPLATE_VAR_SQR.lastIndex = 0;
      // Since this iterates on the same filter and the schema will remain the same do schema calc once
      const schemaPathToUse = templatePath.replace(
        TEMPLATE_VAR_SQR,
        (_, ...captureGroups) => `[${captureGroups[0]}]`
      );

      return Object.keys(templateParentPointer).map((templateKey) => {
        TEMPLATE_VAR_REGEX_NON.lastIndex = 0;

        const transformedString = templatePath.replace(
          TEMPLATE_VAR_REGEX_NON,
          templateKey
        );

        TEMPLATE_VAR_REGEX_NON.lastIndex = 0;
        let inputStringTransformed = inputPath
          ? inputPath.replace(TEMPLATE_VAR_REGEX_NON, templateKey)
          : undefined;

        return [
          transformedString,
          templateKey,
          templateParentPointer,
          schemaPathToUse,
          inputStringTransformed,
        ];
      });
    }

    async function processTemplate(
      path: string,
      key: string,
      value: TransformationConfig,
      templateParentNode?: SchemaPointerTable[string]
    ) {
      if (configValues.debug) {
        console.log("From template");
      }

      if (typeof value !== "object") {
        throw new TypeError(
          "Template expects object as value in paths. Found [" + value + "]"
        );
      }

      const [entryValues, _, otherProps] = extractEntryValues(value, path);
      const otherPropArr = Object.keys(otherProps);
      TEMPLATE_VAR_REGEX_NON.lastIndex = 0;
      // Will retrieve $.some.x[#!y!#] | $.some -> $.some.x
      const [inputTemplateParent, outputPointer, inputPath] = await Promise.all(
        await getTemplateInputNode(path, key, entryValues, templateParentNode)
      );
      const schemaPath = getSchemaPath(path);
      const schemaShape = await fetchValueFromSchema(schemaPath);

      const processTemplateEntry = expandTemplateVariables(
        inputTemplateParent.data as Pointer,
        path
      ).map(([targetPath, targetKey, inputNodeData, schemaPath, altInput]) => {
        const newValue = {
          ...schemaShape.data,
          ...inputNodeData[targetKey],
        };

        outputPointer.data[targetKey] = newValue;
        const childPointer: PointerTable[string] = {
          data: newValue,
          parentProperty: targetKey,
          arrays: [],
          parent: outputPointer as unknown as Pointer,
        };

        sessionTable.setOutputPath(targetPath, childPointer);

        return otherPropArr.map((otherProp) => [
          value,
          otherProp,
          otherProps?.[otherProp],
          `${targetPath}.${otherProp}`,
          undefined,
          schemaPath,
          altInput,
        ]);
      });

      return Promise.all(processTemplateEntry).then((res) => res.flat());
    }

    async function processFilter(
      path: string,
      key: string,
      value: TransformationConfig,
      filterParent?: SchemaPointerTable[string]
    ) {
      if (configValues.debug) {
        console.log("From filter");
      }
      if (typeof value !== "object") {
        throw new TypeError(
          `Filter expects object as value in paths. Found [${value}]`
        );
      }

      const [entryValues, _, otherProps] = extractEntryValues(value, path);
      const otherPropArr = Object.keys(otherProps);
      const inputNode = await getFilterInputNode(
        path,
        key,
        entryValues,
        filterParent
      );
      const schemaShape = await fetchValueFromSchema(getSchemaPath(path));
      const filterPromises = expandFilterVariables(
        inputNode?.data as Pointer[],
        TEMPLATE_VAR_REGEX,
        path
      ).map(([basePath, currentInputNode, schemaPathToUse]) => {
        return fetchMemoizedPath(basePath, undefined, schemaPathToUse)
          .then((pointer): [string, Pointer, Pointer, string | undefined] => {
            if (!pointer) {
              throw new Error(
                "Expected pointer from memoized path to be defined"
              );
            }

            return [
              basePath,
              currentInputNode,
              pointer as Pointer,
              schemaPathToUse,
            ];
          })
          .then(([path, currentInputNode, pointer, schemaPathToUse]) =>
            otherPropArr.map((otherProp) => {
              const subFilter: string | undefined = isFilter(otherProp)
                ? otherProp.split(IS_JSONPATH_FILTER_REGEX)[0]
                : undefined;

              if (
                subFilter === undefined &&
                currentInputNode[otherProp] !== undefined
              ) {
                // @ts-ignore
                pointer.data[otherProp] =
                  // @ts-ignore
                  schemaShape?.data[otherProp] &&
                  // @ts-ignore
                  typeof schemaShape?.data[otherProp] === "object" &&
                  typeof currentInputNode[otherProp] === "object"
                    ? {
                        // @ts-ignore
                        ...schemaShape.data[otherProp],
                        // @ts-ignore
                        ...currentInputNode[otherProp],
                      }
                    : currentInputNode[otherProp];
              }

              // This object will create a new entry in the stack to be used to build out the filter/template nodes
              return [
                value,
                otherProp,
                otherProps[otherProp],
                `${path}.${otherProp}`,
                currentInputNode?.[subFilter as keyof typeof currentInputNode]
                  ? {
                      data: currentInputNode[
                        subFilter as keyof typeof currentInputNode
                      ],
                    }
                  : undefined,
                schemaPathToUse,
              ];
            })
          );
      });

      return Promise.all(filterPromises).then((res) => res.flat());
    }

    // Note for tomorrow me, need to use parents NOT current value to go by pointer!
    /**
     * @param {String} path
     * @param {Object} parent
     * @param {Object | String | undefined} value
     * @param {Object | undefined} filterParent
     * @param {Object} inputs
     */
    async function processNode(
      path: string,
      key: string,
      value: TransformationConfig,
      filterParent?: SchemaPointerTable[string],
      schemaPath?: string,
      inputPath?: string
    ) {
      switch (typeof value) {
        case "object":
          return isFilter(key)
            ? processFilter(path, key, value, filterParent)
            : isTemplate(key)
              ? processTemplate(path, key, value, filterParent)
              : processObjectNode(value, path);
        case "string":
        case "undefined":
          // Below here the hierarch of value is string, so must be an inputPath
          // Oh not defined, then did we get a passed inputPath from a filter or template?
          // None then must be the same shape!
          return setWithoutEntry(value ?? inputPath ?? path, path, schemaPath);
        default:
          throw new TypeError("Expected to be Object, String, or Undefined");
      }
    }

    function setInputs(inputs = {}) {
      configValues.inputs = inputs;
      sessionTable.resetInputTable();
    }

    let stack: TransformationStackEntry[] = [];
    // Might need to overload this for it to evaluate correctly smh
    async function processObject<I extends Json>(
      inputs: I,
      pathsObj: TransformationConfigObject
    ) {
      if (configValues.debug === true) {
        console.log(
          "%c pathsObj: " + JSON.stringify(pathsObj, null, 2),
          "background:pink;"
        );
        console.log(
          "%c inputs: " + JSON.stringify(inputs, null, 2),
          "background:lightblue;"
        );
      }

      setInputs(inputs);
      const firstKeys = Object.keys(pathsObj).map<TransformationStackEntry>(
        (pathKey) => [
          undefined,
          pathKey,
          pathsObj[pathKey],
          pathKey,
          undefined,
          undefined,
          undefined,
        ]
      );

      stack = firstKeys;
      while (stack.length > 0) {
        const [_, key, valueAtKey, path, filterParent, schemaPath, inputPath] =
          stack.pop() as TransformationStackEntry;
        try {
          await processNode(
            path,
            key,
            valueAtKey,
            filterParent,
            schemaPath,
            inputPath
          ).then((otherObjects) => {
            if (otherObjects && Array.isArray(otherObjects)) {
              stack.push(...(otherObjects as TransformationStackEntry[]));
            }
          });
        } catch (e) {
          if (configValues.debug) {
            console.error(e);
          }
        }
      }
    }

    // If this proves too slow, I'll switch to a tombstone based system
    function resetCollections() {
      const { pathTable, outputPathTable } = sessionTable.all();
      const outputPathKeys = Object.keys(outputPathTable);

      // Will iterate over all filters and search in outputPaths for pointers that are using them and set all to undefined
      Object.keys(pathTable).forEach((pathToReset) => {
        outputPathKeys
          .filter((outputPathKey) => outputPathKey.includes(pathToReset))
          .forEach((outputPathKeyToReset) => {
            const outputPathPointer = outputPathTable[outputPathKeyToReset];
            if (outputPathPointer?.parent !== undefined) {
              if (Array.isArray(outputPathPointer.parent)) {
                outputPathPointer.parent.length = 0;
              } else {
                outputPathPointer.parent = undefined as unknown as Pointer;
              }

              outputPathTable[outputPathKeyToReset] =
                undefined as unknown as PointerTableEntry;
            }
          });

        if (pathTable[pathToReset] === undefined) {
          return;
        }

        let { parent, parentProperty } = pathTable[pathToReset];
        if (parent !== undefined && parentProperty !== undefined) {
          const valueAtParent = parent[parentProperty];
          if (Array.isArray(valueAtParent)) {
            valueAtParent.length = 0;
          }
          pathTable[pathToReset] = undefined as unknown as PointerTableEntry;
        }
      });
    }

    return {
      setInputs,
      processObject,
      resetCollections,
      setDebug(value: boolean) {
        configValues.debug = value;
        console.log("Debug set to: ", value);
      },
      setSchema<T extends S>(schema: T) {
        configValues.schema = schema;
      },
      getConfigValues() {
        return configValues;
      },
      list() {
        return sessionTable.all();
      },
      resetInputTable: sessionTable.resetInputTable,
    };
  })(jsonSrc, schemaObj);
}
